import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, User, ArrowRight, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";

export default function BlogDetail() {
  const { slug } = useParams();
  const { t, lang, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => {
      setPost(r.data);
      api.get("/blog").then((rr) => setRelated(rr.data.filter((p) => p.slug !== slug).slice(0, 3)));
    }).catch(() => setPost(null));
  }, [slug]);

  if (!post) return <div className="py-32 text-center text-slate-500">Loading...</div>;

  const title = lang === "ar" ? post.title_ar : post.title_en;
  const content = lang === "ar" ? post.content_ar : post.content_en;

  return (
    <div data-testid="blog-detail-page">
      <section className="bg-slate-50 py-12">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/blog" data-testid="blog-back" className="text-[#0891b2] text-sm inline-flex items-center gap-2 mb-6">
            <Arrow className="w-4 h-4 rotate-180" /> {t.blog.backToAll}
          </Link>
          <div className="text-[11px] uppercase tracking-widest text-[#ef4444] mb-3 font-semibold">{post.category}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight mb-5" data-testid="blog-post-title">{title}</h1>
          <div className="flex items-center gap-5 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><User className="w-4 h-4" />{post.author}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{new Date(post.published_at).toLocaleDateString()}</span>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-3xl mx-auto px-6">
          <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
            <img src={post.featured_image} alt={title} className="w-full h-full object-cover" />
          </div>
          <article className="prose-mariot text-slate-700 text-lg leading-relaxed whitespace-pre-line">
            {content}
          </article>

          <div className="mt-10 p-6 bg-[#0891b2] rounded-lg text-white text-center">
            <p className="mb-4 text-cyan-100">{t.ctaSection.subtitle}</p>
            <Link to="/quote">
              <Button className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-11 px-8">
                {t.ctaSection.button}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <h2 className="font-display text-2xl text-[#0891b2] mb-6">{t.blog.relatedTitle}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="bg-white border border-slate-200 hover:border-[#0891b2] rounded-lg overflow-hidden transition-colors" data-testid={`related-blog-${i}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.featured_image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-[#ef4444] mb-2">{p.category}</div>
                    <h3 className="font-display text-base text-slate-900 line-clamp-2">{lang === "ar" ? p.title_ar : p.title_en}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
